/**
 * PermissionsPage — Admin → Permissions (2026-07-29)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * One screen for "who may do what", replacing six hardcoded role lists that had
 * drifted apart in the backend.
 *
 * Two layers, shown together so the answer to "why can this person do that?" is
 * always on screen:
 *   • ROLE GRID   — role x permission tick-boxes (Option B). A cell left on
 *                   "Default" uses the legacy built-in value; the legacy value
 *                   is printed next to it so you can see what you're changing.
 *   • PER-PERSON  — pick anyone and set each permission to Inherit / On / Off
 *                   (Option A). Inherit follows the grid above.
 *
 * Nothing here changes behaviour until a cell is actually set: an unconfigured
 * grid resolves to exactly the pre-existing hardcoded defaults.
 */

import { useEffect, useMemo, useState } from 'react';
import { api } from '../../utils/api';

type Cell = { configured: boolean | null; legacy: boolean; effective: boolean };
type PermMeta = {
  key: string;
  label: string;
  description: string;
  category: string;
  /**
   * Roles this permission is even OFFERED for (spec §4: `field_agent` is
   * offered only for coordinator / sales_rep). Null == every role. UI
   * affordance only — the backend resolves an existing row regardless.
   */
  offeredForRoles?: string[] | null;
};

/** Is this permission offered for a role? Null/absent list == offered to all. */
const offeredFor = (meta: PermMeta, roleName: string | null | undefined): boolean =>
  !Array.isArray(meta.offeredForRoles) ||
  meta.offeredForRoles.length === 0 ||
  (roleName != null && meta.offeredForRoles.includes(roleName));
type RoleRow = {
  roleId: string;
  roleName: string;
  displayName: string;
  activeUsers: number;
  permissions: Record<string, Cell>;
};
type UserPerm = {
  state: 'inherit' | 'on' | 'off';
  inheritedValue: boolean;
  effective: boolean;
  decidedBy: 'user' | 'role' | 'legacy';
};

export default function PermissionsPage() {
  const [catalog, setCatalog] = useState<PermMeta[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);

  // Per-person panel
  const [people, setPeople] = useState<Array<{ id: string; name: string; email: string; roleName: string | null; roleDisplayName?: string | null }>>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userPerms, setUserPerms] = useState<Record<string, UserPerm> | null>(null);
  const [userBusy, setUserBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/permissions');
      setCatalog(res.data.data.catalog);
      setRoles(res.data.data.roles);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // SUN-42: login users of this company (users.id + role), from the same
  // admin router as the grid. GET /users is the EMPLOYEE directory — employee
  // ids, no role, limit capped at 100 — so it could never feed this picker.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/permissions/people');
        setPeople(res.data?.data?.people ?? []);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Could not load the list of people');
      }
    })();
  }, []);

  const loadUser = async (userId: string) => {
    setSelectedUserId(userId);
    setUserPerms(null);
    if (!userId) return;
    setUserBusy(true);
    try {
      const res = await api.get(`/admin/permissions/users/${userId}`);
      setUserPerms(res.data.data.permissions);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load that person');
    } finally {
      setUserBusy(false);
    }
  };

  const setRoleCell = async (role: RoleRow, key: string, next: boolean | null) => {
    const id = `${role.roleId}:${key}`;
    setSavingCell(id);
    try {
      if (next === null) {
        await api.delete(`/admin/permissions/roles/${role.roleId}/${key}`);
      } else {
        await api.put(`/admin/permissions/roles/${role.roleId}`, {
          permissionKey: key, enabled: next,
        });
      }
      setRoles((prev) =>
        prev.map((r) =>
          r.roleId !== role.roleId
            ? r
            : {
                ...r,
                permissions: {
                  ...r.permissions,
                  [key]: {
                    ...r.permissions[key],
                    configured: next,
                    effective: next === null ? r.permissions[key].legacy : next,
                  },
                },
              },
        ),
      );
      if (selectedUserId) loadUser(selectedUserId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not save that change');
    } finally {
      setSavingCell(null);
    }
  };

  const setUserState = async (key: string, state: 'inherit' | 'on' | 'off') => {
    if (!selectedUserId) return;
    setUserBusy(true);
    try {
      await api.put(`/admin/permissions/users/${selectedUserId}`, { permissionKey: key, state });
      await loadUser(selectedUserId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not save that switch');
    } finally {
      setUserBusy(false);
    }
  };

  const selectedPerson = useMemo(
    () => people.find((p) => p.id === selectedUserId) ?? null,
    [people, selectedUserId],
  );

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading permissions…</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900">Permissions</h1>
      <p className="mt-1 max-w-3xl text-sm text-gray-600">
        Who is allowed to do what. A permission left on <strong>Default</strong> keeps the
        behaviour the system shipped with — shown in grey next to each box. Individual people
        can be given an exception in the panel below.
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Role grid ─────────────────────────────────────────────────────── */}
      <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="px-4 py-3 font-semibold text-gray-700">Role</th>
              {catalog.map((c) => (
                <th key={c.key} className="px-4 py-3 font-semibold text-gray-700">
                  <div>{c.label}</div>
                  <div className="mt-0.5 max-w-[16rem] text-[11px] font-normal leading-snug text-gray-500">
                    {c.description}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.roleId} className="border-b border-gray-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{r.displayName}</div>
                  <div className="text-[11px] text-gray-500">
                    {r.roleName} · {r.activeUsers} active
                  </div>
                </td>
                {catalog.map((c) => {
                  const cell = r.permissions[c.key];
                  const busy = savingCell === `${r.roleId}:${c.key}`;
                  const value = cell.configured === null ? 'default' : cell.configured ? 'on' : 'off';
                  // Backend sends `offeredForRoles` for exactly this purpose
                  // (spec §4 — field_agent is offered only for coordinator /
                  // sales_rep), but no client read it, so the tag was shown
                  // for manager rows where it means nothing. A row already
                  // configured off-list stays editable so it can be undone.
                  if (!offeredFor(c, r.roleName) && cell.configured === null) {
                    return (
                      <td key={c.key} className="px-4 py-3">
                        <span className="text-xs text-gray-400">
                          Not offered for this role
                        </span>
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className="px-4 py-3">
                      <select
                        value={value}
                        disabled={busy}
                        aria-label={`${r.displayName} — ${c.label}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRoleCell(r, c.key, v === 'default' ? null : v === 'on');
                        }}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
                      >
                        <option value="default">
                          Default ({cell.legacy ? 'allowed' : 'not allowed'})
                        </option>
                        <option value="on">Allowed</option>
                        <option value="off">Not allowed</option>
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Per-person exceptions ─────────────────────────────────────────── */}
      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="text-lg font-semibold text-gray-900">Exceptions for one person</h2>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Use this when someone needs to differ from their role — a new manager who
          shouldn&apos;t have overrides yet, or one salesperson who should. Leave a switch on{' '}
          <strong>Inherit</strong> to follow the grid above.
        </p>

        <select
          value={selectedUserId}
          onChange={(e) => loadUser(e.target.value)}
          className="mt-4 w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Choose a person…</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.roleDisplayName ?? p.roleName ?? 'no role'}
            </option>
          ))}
        </select>

        {selectedUserId && userPerms && (
          <div className="mt-5 space-y-3">
            {catalog.map((c) => {
              const up = userPerms[c.key];
              if (!up) return null;
              // Same affordance as the grid: a permission not offered for this
              // person's role reads as such, unless an exception already
              // exists — that stays editable so it can be undone.
              if (!offeredFor(c, selectedPerson?.roleName) && up.state === 'inherit') {
                return (
                  <div
                    key={c.key}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3"
                  >
                    <div className="min-w-[16rem] flex-1">
                      <div className="text-sm font-medium text-gray-900">{c.label}</div>
                      <div className="text-[11px] text-gray-500">
                        Not offered for the {selectedPerson?.roleName ?? 'current'} role
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={c.key}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3"
                >
                  <div className="min-w-[16rem] flex-1">
                    <div className="text-sm font-medium text-gray-900">{c.label}</div>
                    <div className="text-[11px] text-gray-500">
                      {up.state === 'inherit'
                        ? `Following their role — currently ${up.inheritedValue ? 'allowed' : 'not allowed'}`
                        : `Set for this person — ${up.effective ? 'allowed' : 'not allowed'}`}
                    </div>
                  </div>
                  <select
                    value={up.state}
                    disabled={userBusy}
                    aria-label={`${selectedPerson?.name ?? 'person'} — ${c.label}`}
                    onChange={(e) => setUserState(c.key, e.target.value as any)}
                    className="rounded-lg border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
                  >
                    <option value="inherit">
                      Inherit ({up.inheritedValue ? 'allowed' : 'not allowed'})
                    </option>
                    <option value="on">Allowed</option>
                    <option value="off">Not allowed</option>
                  </select>
                </div>
              );
            })}
          </div>
        )}
        {selectedUserId && userBusy && !userPerms && (
          <div className="mt-4 text-sm text-gray-500">Loading…</div>
        )}
      </div>
    </div>
  );
}
